/**
 * Version checking utility to detect app updates and prompt user to refresh
 */

// Store current version in a meta tag or build-time constant
const getCurrentVersion = () => {
  // Try to get version from meta tag (set during build)
  const metaTag = document.querySelector('meta[name="app-version"]');
  if (metaTag) {
    return metaTag.getAttribute('content');
  }
  // Fallback: use build timestamp or hash from index.html
  return document.querySelector('script[src*="index"]')?.src?.match(/[a-f0-9]{8,}/)?.[0] || 'unknown';
};

// Check for new version by fetching index.html and comparing
export const checkForUpdates = async () => {
  try {
    const response = await fetch('/?v=' + Date.now(), { 
      method: 'HEAD',
      cache: 'no-store' 
    });
    
    // Get version from response headers or ETag
    const etag = response.headers.get('etag');
    const lastModified = response.headers.get('last-modified');
    
    // Store current version in sessionStorage
    const currentVersion = getCurrentVersion();
    const storedVersion = sessionStorage.getItem('app_version');
    
    // If version changed, trigger update
    if (storedVersion && storedVersion !== currentVersion) {
      return { hasUpdate: true, currentVersion, storedVersion };
    }
    
    // Store current version
    if (!storedVersion) {
      sessionStorage.setItem('app_version', currentVersion);
    }
    
    return { hasUpdate: false };
  } catch (error) {
    console.warn('[VersionCheck] Failed to check for updates:', error);
    return { hasUpdate: false, error };
  }
};

// More reliable: check by fetching a version endpoint or index.html content
export const checkForUpdatesByFetch = async () => {
  try {
    // Fetch index.html with cache busting
    const response = await fetch('/?v=' + Date.now(), {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache'
      }
    });
    
    if (!response.ok) return { hasUpdate: false };
    
    const html = await response.text();
    
    // Extract script src from the HTML
    const scriptMatch = html.match(/<script[^>]+src=["']([^"']*index[^"']*)["']/);
    if (!scriptMatch) return { hasUpdate: false };
    
    const scriptSrc = scriptMatch[1];
    const hashMatch = scriptSrc.match(/[a-f0-9]{8,}/);
    const newHash = hashMatch ? hashMatch[0] : scriptSrc;
    
    // Compare with current loaded script
    const currentScript = document.querySelector('script[src*="index"]')?.src || '';
    const currentHash = currentScript.match(/[a-f0-9]{8,}/)?.[0] || '';
    
    if (newHash && currentHash && newHash !== currentHash) {
      return { hasUpdate: true, currentHash, newHash };
    }
    
    return { hasUpdate: false };
  } catch (error) {
    console.warn('[VersionCheck] Failed to check for updates:', error);
    return { hasUpdate: false, error };
  }
};

// Start periodic version checking
export const startVersionCheck = (onUpdateAvailable, intervalMs = 60000) => {
  let intervalId = null;
  
  // Check immediately
  checkForUpdatesByFetch().then(result => {
    if (result.hasUpdate) {
      onUpdateAvailable(result);
      // Don't start interval if update already detected
      return;
    }
    
    // Then check periodically
    intervalId = setInterval(async () => {
      const result = await checkForUpdatesByFetch();
      if (result.hasUpdate) {
        onUpdateAvailable(result);
        if (intervalId) {
          clearInterval(intervalId); // Stop checking after update detected
          intervalId = null;
        }
      }
    }, intervalMs);
  });
  
  return () => {
    if (intervalId) {
      clearInterval(intervalId);
    }
  };
};

